import { apiClient } from "@base/api/client";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

const workHistoryDailyStatSchema = z.object({
  daily_log_id: z.number().nullable(),
  date: z.string(),
  day_of_week: z.string(),
  workforce_quantity: z.number(),
  equipment_quantity: z.number(),
});

const workHistorySummaryResponseSchema = z.object({
  summary: z.object({
    total_workforce: z.number(),
    total_equipment: z.number(),
  }),
  daily_stats: z.array(workHistoryDailyStatSchema),
});

export type WorkHistoryDailyStat = z.infer<typeof workHistoryDailyStatSchema>;
export type WorkHistorySummaryResponse = z.infer<typeof workHistorySummaryResponseSchema>;

async function fetchWorkHistorySummary(
  projectId: string,
  startDate: string,
  endDate: string,
): Promise<WorkHistorySummaryResponse> {
  const response = await apiClient.get<unknown>("/project-management/api/analytics/summary", {
    params: { project_id: projectId, start_date: startDate, end_date: endDate },
  });
  return workHistorySummaryResponseSchema.parse(response.data);
}

export function workHistorySummaryQueryOptions(projectId: string, startDate: string, endDate: string) {
  return {
    queryKey: ["work-history-summary", projectId, startDate, endDate] as const,
    queryFn: () => fetchWorkHistorySummary(projectId, startDate, endDate),
  };
}

export function useWorkHistorySummaryQuery(
  projectId: string,
  startDate: string | undefined,
  endDate: string | undefined,
  options?: Omit<UseQueryOptions<WorkHistorySummaryResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({
    ...workHistorySummaryQueryOptions(projectId, startDate ?? "", endDate ?? ""),
    enabled: !!startDate && !!endDate,
    ...options,
  });
}
